
import Vue from 'vue';
import type { PropOptions } from 'vue';
import { mapGetters, mapState } from 'vuex';
import PublicPortfolioClient from './PublicPortfolioClient.vue';
import type { Client, PublicPortfolioConfiguration } from '@/types/graphql';
import PortfolioButton from '@/components/partials/public-portfolio/PortfolioButton/PortfolioButton.vue';
import PortfolioCurrentUserQuery from '@/apollo/queries/Portfolio/PortfolioCurrentUser.query';
import PortfolioVisibilityIndicator from '@/components/partials/public-portfolio/PortfolioVisibilityIndicator/PortfolioVisibilityIndicator.vue';
import PublicPortfolioHeadlineModal from '~/components/partials/public-portfolio/PublicPortfolioClients/PublicPortfolioHeadlineModal.vue';
import PublicPortfolioClientsModal from '~/components/partials/public-portfolio/PublicPortfolioClients/PublicPortfolioClientsModal.vue';
import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import PublicPortfolioAddClientsModal from '~/components/partials/public-portfolio/PublicPortfolioClients/PublicPortfolioAddClientsModal.vue';
import BravadoCallout from '~/components/common/BravadoCallout/BravadoCallout.vue';
import type { SectionMode } from '~/components/partials/public-portfolio/types';
import publicPortfolioSectionMixin from '~/components/partials/public-portfolio/mixins/publicPortfolioSectionMixin';
import { sortByField } from '~/components/utils/sorting_func';

const importSwiper = () => import('swiper');
const ROW_COUNT = 1;
const ROW_COUNT_MOBILE = 1;

export default Vue.extend({
  name: 'PublicPortfolioClientsSection',
  components: {
    PublicPortfolioAddClientsModal,
    PublicPortfolioClientsModal,
    PortfolioButton,
    PublicPortfolioHeadlineModal,
    PublicPortfolioClient,
    BravadoButtonV3,
    BravadoCallout,
    PortfolioVisibilityIndicator,
  },
  mixins: [publicPortfolioSectionMixin],
  props: {
    mode: {
      type: String,
      default: 'view',
    } as PropOptions<SectionMode>,
    configuration: {
      type: Object,
      required: true,
    } as PropOptions<PublicPortfolioConfiguration>,
  },
  apollo: {
    currentUser: {
      query: PortfolioCurrentUserQuery,
      update(data) {
        this.clients = data.currentUser?.solidClients;
      },
      skip(): boolean {
        return !this.$auth.loggedIn;
      },
    },
  },
  data(): { clients: Client[]; swiperInstance: null | { update: () => void } } {
    return {
      clients: [],
      swiperInstance: null,
    };
  },
  computed: {
    ...mapState('portfolio', ['user']),
    ...mapGetters('portfolio', ['hasDescription', 'visibleClientsPublic']),
    visibleClients(): Client[] {
      if (this.mode === 'edit') {
        return this.clients
          .filter(client => client.isPublic)
          .sort(sortByField('position'))
          .map(client => ({
            ...client,
            avatar_url: client.company.logoUrl,
            name: client.company.name,
            company_slug: client.company.slug,
            company_pages_slug: client.company.companyPagesSlug,
          }));
      }

      return this.visibleClientsPublic;
    },
    hasClients(): boolean {
      return Boolean(this.visibleClients.length);
    },
    isEdit(): boolean {
      return this.mode === 'edit';
    },
    rootClass(): Record<string, boolean> {
      return {
        'public-portfolio-clients-section': true,
        [`public-portfolio-clients-section--${this.mode}`]: true,
      };
    },
    clientsContainerClass(): Record<string, boolean> {
      return {
        'public-portfolio-clients-section__clients-container': true,
        'public-portfolio-clients-section__clients-container--empty': !this.visibleClients.length,
      };
    },
    descriptionClass(): Record<string, boolean> {
      return {
        'public-portfolio-clients-section__description': true,
        'public-portfolio-clients-section__description--empty': !this.user.description,
      };
    },
    headClass(): Record<string, boolean> {
      return {
        'public-portfolio-clients-section__head': true,
        'public-portfolio-clients-section__head--standalone':
          !this.configuration.sectionClientsIsPublic || !this.hasClients,
      };
    },
    bodyClass(): Record<string, boolean> {
      return {
        'public-portfolio-clients-section__body': true,
        'public-portfolio-clients-section__body--standalone':
          !this.user.description || !this.configuration.sectionHeadlineIsPublic,
      };
    },
    userDescription(): string {
      return this.user.description || 'This is your chance to make a great first impression...';
    },
    nextButtonClass(): Record<string, boolean> {
      return {
        'public-portfolio-clients-section__next-button': true,
        'public-portfolio-clients-section__next-button--hidden': true,
      };
    },
    prevButtonClass(): Record<string, boolean> {
      return {
        'public-portfolio-clients-section__prev-button': true,
        'public-portfolio-clients-section__prev-button--hidden': true,
      };
    },
  },
  watch: {
    visibleClients(newValue, oldValue) {
      if (newValue.length === oldValue.length) return;

      this.$nextTick(() => {
        try {
          this.swiperInstance?.update();
        } catch (e) {
          this.$sentryUtils.error(e);
        }
      });
    },
  },
  async mounted() {
    const { Swiper, Navigation, Mousewheel, Grid } = await importSwiper();

    Swiper.use([Navigation, Mousewheel, Grid]);
    this.swiperInstance = new Swiper('.public-portfolio-clients-section__clients-list', {
      grid: {
        rows: this.$device.isMobile ? ROW_COUNT_MOBILE : ROW_COUNT,
        fill: 'row',
      },
      slidesPerView: 'auto',
      freeMode: true,
      mousewheel: {
        forceToAxis: true,
      },
      navigation: {
        nextEl: '.public-portfolio-clients-section__next-button',
        prevEl: '.public-portfolio-clients-section__prev-button',
      },
    });

    this.$nextTick(() => {
      (this.$refs.prevButton as HTMLElement)?.classList.remove('public-portfolio-clients-section__prev-button--hidden');
      (this.$refs.nextButton as HTMLElement)?.classList.remove('public-portfolio-clients-section__next-button--hidden');
    });
  },
});
